import { type WorkflowId, type WorkflowNodeId } from '@blaze-shared/api-contracts-domain';

export type WorkflowErrorType =
  | 'COLUMN_MISSING_ERROR'
  | 'WORKFLOW_GRAPH_INTEGRITY_ERROR'
  | 'DATASET_MISSING_ERROR'
  | 'INPUT_MISSING_ERROR'
  | 'INVALID_FIELD_VALUE_ERROR'
  | 'INVALID_FORMULA_ERROR'
  | 'NODE_MISSING_ERROR'
  | 'NODE_NOT_LAST_ERROR'
  | 'PARAM_MISSING_ERROR'
  | 'SCOPE_VAR_MISSING_ERROR'
  | 'START_NODE_MISSING_ERROR'
  | 'TAG_MISSING_ERROR';

export abstract class WorkflowError<T extends WorkflowErrorType> {
  protected constructor(
    public readonly errorType: T,
    public readonly workflowId: WorkflowId,
    public readonly nodeId: WorkflowNodeId,
    public readonly errorMessage: string,
    public readonly sourceId?: string,
    public readonly connectionId?: string,
  ) {}
}

export class ColumnMissingWorkflowError extends WorkflowError<'COLUMN_MISSING_ERROR'> {
  constructor(
    workflowId: WorkflowId,
    public readonly columns: string[],
    nodeId: WorkflowNodeId,
    public readonly datasetId?: string,
    sourceId?: string,
  ) {
    super(
      'COLUMN_MISSING_ERROR',
      workflowId,
      nodeId,
      `Missing column ${columns.join('.')} in node ${nodeId} in workflow ${workflowId}`,
      sourceId,
    );
  }
}

export class DatasetMissingWorkflowError extends WorkflowError<'DATASET_MISSING_ERROR'> {
  constructor(workflowId: WorkflowId, nodeId: WorkflowNodeId, sourceId?: string) {
    super(
      'DATASET_MISSING_ERROR',
      workflowId,
      nodeId,
      `Missing dataset in node ${nodeId}`,
      sourceId,
    );
  }
}

export class DatasetNotAvailableWorkflowError extends WorkflowError<'DATASET_MISSING_ERROR'> {
  constructor(workflowId: WorkflowId, nodeId: WorkflowNodeId, sourceId?: string) {
    super(
      'DATASET_MISSING_ERROR',
      workflowId,
      nodeId,
      `The dataset in node ${nodeId} is not available in the current project.`,
      sourceId,
    );
  }
}

export class InputMissingWorkflowError extends WorkflowError<'INPUT_MISSING_ERROR'> {
  constructor(
    workflowId: WorkflowId,
    nodeId: WorkflowNodeId,
    public readonly inputName: string,
    sourceId?: string,
  ) {
    super(
      'INPUT_MISSING_ERROR',
      workflowId,
      nodeId,
      `Missing variable ${inputName} in node ${nodeId}`,
      sourceId,
    );
  }
}

export class ParamMissingWorkflowError extends WorkflowError<'PARAM_MISSING_ERROR'> {
  constructor(
    workflowId: WorkflowId,
    public readonly param: string,
    nodeId: WorkflowNodeId,
    sourceId?: string,
  ) {
    super(
      'PARAM_MISSING_ERROR',
      workflowId,
      nodeId,
      `Missing param: ${param} in node ${nodeId}`,
      sourceId,
    );
  }
}

export class NodeNotLastWorkflowError extends WorkflowError<'NODE_NOT_LAST_ERROR'> {
  constructor(
    workflowId: WorkflowId,
    nodeId: WorkflowNodeId,
    public readonly nodeName: string,
    sourceId?: string,
  ) {
    super(
      'NODE_NOT_LAST_ERROR',
      workflowId,
      nodeId,
      `${nodeName} node ${nodeId} should end the workflow`,
      sourceId,
    );
  }
}

export class TagMissingWorkflowError extends WorkflowError<'TAG_MISSING_ERROR'> {
  constructor(
    workflowId: WorkflowId,
    public readonly tagId: string,
    nodeId: WorkflowNodeId,
    public readonly datasetId?: string,
    sourceId?: string,
  ) {
    super(
      'TAG_MISSING_ERROR',
      workflowId,
      nodeId,
      `Missing tag ${tagId} in node ${nodeId}`,
      sourceId,
    );
  }
}

export class WorkflowGraphIntegrityError extends WorkflowError<'WORKFLOW_GRAPH_INTEGRITY_ERROR'> {
  constructor(workflowId: WorkflowId, errorMessage: string, nodeId?: WorkflowNodeId) {
    super('WORKFLOW_GRAPH_INTEGRITY_ERROR', workflowId, nodeId ?? '', errorMessage);
  }
}

export class OrphanedFunctionOutputWorkflowError extends WorkflowError<'INVALID_FIELD_VALUE_ERROR'> {
  constructor(
    workflowId: WorkflowId,
    nodeId: WorkflowNodeId,
    public readonly orphanedVariableIds: string[],
    sourceId?: string,
  ) {
    const count = orphanedVariableIds.length;
    super(
      'INVALID_FIELD_VALUE_ERROR',
      workflowId,
      nodeId,
      count === 1
        ? 'Function output mapping is outdated. Remove the mapping for the deleted output variable.'
        : `Function output mappings are outdated. Remove mappings for ${count} deleted output variables.`,
      sourceId,
    );
  }
}
